import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useUI } from '../../context/UIContext';
import { Link } from 'react-router-dom';
import Pagination from '../../components/common/Pagination';
import SortableHeader from '../../components/common/SortableHeader';
import * as XLSX from 'xlsx';
import { useSocket } from '../../context/SocketContext';

const StockManagement = () => {
    const { user } = useAuth();
    const { showToast } = useUI();
    const { socket } = useSocket();
    const [movements, setMovements] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterType, setFilterType] = useState('Tous');
    const [filterUser, setFilterUser] = useState('Tous');
    const [filterWarehouse, setFilterWarehouse] = useState('Tous');
    const [usersList, setUsersList] = useState([]);
    const [warehousesList, setWarehousesList] = useState([]);
    const [showFilters, setShowFilters] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalMovements, setTotalMovements] = useState(0);
    const [limit, setLimit] = useState(parseInt(localStorage.getItem('app_items_per_page')) || 25);
    const [sortBy, setSortBy] = useState('createdAt');
    const [sortOrder, setSortOrder] = useState('DESC');

    // Fetch supporting data for filters
    useEffect(() => {
        if (!user?.companyId) return;

        const fetchFilterData = async () => {
            try {
                const [uRes, wRes] = await Promise.all([
                    fetch(`/api/users/${user.companyId}`),
                    fetch(`/api/warehouses?companyId=${user.companyId}`)
                ]);
                if (uRes.ok) setUsersList(await uRes.json());
                if (wRes.ok) setWarehousesList(await wRes.json());
            } catch (error) {
                console.error("Error fetching filter data:", error);
            }
        };
        fetchFilterData();
    }, [user?.companyId]);

    const handleExport = () => {
        try {
            showToast("Génération de l'export de l'historique...", "info");
            const exportData = movements.map(m => ({
                'Référence': m.id,
                'Date': new Date(m.createdAt).toLocaleString('fr-FR'),
                'Produit': m.product?.name || 'Inconnu',
                'SKU': m.product?.sku || 'N/A',
                'Type': m.type,
                'Quantité': m.quantity,
                'Entrepôt': m.warehouse?.name || 'N/A',
                'Emplacement': m.location?.name || 'N/A',
                'Responsable': m.user?.name || 'Système',
                'Commentaire': m.comment || ''
            }));

            const ws = XLSX.utils.json_to_sheet(exportData);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Historique Mouvements');

            const date = new Date().toISOString().slice(0, 10);
            XLSX.writeFile(wb, `Export_Historique_Stocks_${date}.xlsx`);
            showToast("Export réussi", "success");
        } catch (error) {
            console.error('Export error:', error);
            showToast("Erreur lors de l'exportation", "error");
        }
    };

    const fetchMovements = useCallback(async () => {
        if (!user?.companyId) return;
        try {
            setLoading(true);
            const queryParams = new URLSearchParams({
                companyId: user.companyId,
                page: currentPage,
                limit: limit,
                sortBy: sortBy,
                sortOrder: sortOrder
            });

            if (filterType !== 'Tous') queryParams.append('type', filterType);
            if (filterUser !== 'Tous') queryParams.append('userId', filterUser);
            if (filterWarehouse !== 'Tous') queryParams.append('warehouseId', filterWarehouse);

            const response = await fetch(`/api/stock-movements?${queryParams.toString()}`);
            if (response.ok) {
                const data = await response.json();
                if (data && typeof data === 'object' && !Array.isArray(data)) {
                    setMovements(data.movements || []);
                    setTotalPages(data.totalPages || 1);
                    setTotalMovements(data.total || 0);
                } else if (Array.isArray(data)) {
                    setMovements(data);
                    setTotalPages(1);
                    setTotalMovements(data.length);
                } else {
                    setMovements([]);
                }
            } else {
                showToast("Erreur lors du chargement de l'historique", "error");
            }
        } catch (error) {
            console.error('Error fetching movements:', error);
            showToast("Erreur serveur", "error");
        } finally {
            setLoading(false);
        }
    }, [user?.companyId, showToast, currentPage, filterType, filterUser, filterWarehouse, limit, sortBy, sortOrder]);

    useEffect(() => {
        fetchMovements();
    }, [fetchMovements]);

    useEffect(() => {
        if (!socket) return;

        const handleUpdate = () => {
            fetchMovements();
        };

        socket.on('stock_updated', handleUpdate);

        return () => {
            socket.off('stock_updated', handleUpdate);
        };
    }, [socket, fetchMovements]);

    // Reset pagination on filter change
    useEffect(() => {
        setCurrentPage(1);
    }, [filterType, filterUser, filterWarehouse]);

    const filteredMovements = (movements || []).filter(m => {
        if (!m) return false;
        const name = m.product?.name || "";
        const sku = m.product?.sku || "";
        const matchesSearch =
            name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            sku.toLowerCase().includes(searchTerm.toLowerCase());

        return matchesSearch;
    });

    const getTypeColor = (type) => {
        if (type.includes('Entrée')) return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400';
        if (type.includes('Sortie')) return 'bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-400';
        return 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400';
    };

    const getTypeIcon = (type) => {
        if (type.includes('Entrée')) return 'south_west';
        if (type.includes('Sortie')) return 'north_east';
        return 'tune';
    };

    const getPoReference = (desc) => {
        if (!desc) return null;
        const match = desc.match(/commande achat ([a-zA-Z0-9-]+)/i);
        return match ? match[1] : null;
    };

    const getQuantityClass = (m) => {
        if (m.quantity > 0) return 'text-emerald-600 dark:text-emerald-400';
        if (m.quantity < 0) return 'text-rose-600 dark:text-rose-400';
        return 'text-amber-600 dark:text-amber-400';
    };

    return (
        <div className="flex flex-col gap-8">
            {/* Header Section - Sticky */}
            <div className="sticky top-[-16px] lg:top-[-16px] z-30 bg-background-light/80 dark:bg-background-dark/80 backdrop-blur-md pt-4 pb-6 mb-2 flex flex-col md:flex-row justify-between items-start md:items-end gap-4 border-b border-slate-200/50 dark:border-slate-800/50">
                <div className="flex flex-col gap-2">
                    <h1 className="text-3xl font-black tracking-tight bg-gradient-to-r from-primary to-blue-500 bg-clip-text text-transparent drop-shadow-sm">Historique des mouvements</h1>
                    <p className="text-slate-500 dark:text-slate-400">Suivi complet des flux d'inventaire, entrées, sorties et ajustements.</p>
                </div>
                <div className="flex gap-3">
                    <Link to="/stock-scanner" className="flex items-center justify-center rounded-xl h-12 bg-primary text-white hover:bg-primary/90 transition-all gap-2 px-6 text-sm font-bold shadow-lg shadow-primary/20">
                        <span className="material-symbols-outlined text-[20px]">qr_code_scanner</span>
                        <span>Scanner / Entrée-Sortie</span>
                    </Link>
                    {warehousesList.length > 1 && (
                        <Link to="/stocks/transfer" className="flex items-center gap-2 px-6 py-3 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 rounded-xl font-bold shadow-sm transition-all hover:bg-slate-50 dark:hover:bg-slate-700">
                            <span className="material-symbols-outlined text-primary">swap_horiz</span>
                            <span>Transfert</span>
                        </Link>
                    )}
                    <button
                        onClick={handleExport}
                        className="flex items-center gap-2 px-6 py-3 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 rounded-xl font-bold shadow-sm transition-all hover:bg-slate-50 dark:hover:bg-slate-700"
                    >
                        <span className="material-symbols-outlined text-primary">file_download</span>
                        <span>Exporter</span>
                    </button>
                </div>
            </div>

            {/* Barre de Filtres */}
            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl shadow-sm overflow-hidden transition-all">
                <div className="p-6 flex flex-wrap gap-4 items-end border-b border-slate-100 dark:border-slate-800">
                    <div className="flex-1 min-w-[300px]">
                        <label className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2 block">Rechercher</label>
                        <div className="relative group">
                            <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-primary transition-colors">search</span>
                            <input
                                className="w-full bg-slate-100 dark:bg-slate-800 border-none rounded-lg py-2.5 pl-10 pr-4 text-sm focus:ring-2 focus:ring-primary text-slate-900 dark:text-white outline-none"
                                placeholder="SKU, Article..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                    </div>
                    <div className="w-full md:w-auto">
                        <label className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2 block">Type de mouvement</label>
                        <div className="flex bg-slate-100 dark:bg-slate-800 p-1 rounded-lg">
                            {['Tous', 'Entrée', 'Sortie', 'Ajustement'].map(t => (
                                <button
                                    key={t}
                                    onClick={() => setFilterType(t)}
                                    className={`px-4 py-1.5 text-xs font-bold rounded-md transition-all ${filterType === t ? 'bg-white dark:bg-slate-700 shadow-sm text-primary' : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'}`}
                                >
                                    {t === 'Tous' ? 'Tout' : t + (t === 'Ajustement' ? '' : 's')}
                                </button>
                            ))}
                        </div>
                    </div>
                    <button
                        onClick={() => setShowFilters(!showFilters)}
                        className={`h-10 px-4 rounded-lg transition-all flex items-center gap-2 font-bold text-sm ${showFilters ? 'bg-primary text-white shadow-lg shadow-primary/20' : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700'}`}
                    >
                        <span className="material-symbols-outlined text-xl">filter_list</span>
                        <span>{showFilters ? 'Fermer' : 'Plus de filtres'}</span>
                        {(filterUser !== 'Tous' || filterWarehouse !== 'Tous') && !showFilters && (
                            <span className="flex size-2 bg-primary dark:bg-white rounded-full animate-pulse ml-1"></span>
                        )}
                    </button>
                </div>

                {/* Filtres Avancés */}
                {showFilters && (
                    <div className="p-6 bg-slate-50/50 dark:bg-slate-800/30 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 animate-in slide-in-from-top-2 duration-200">
                        <div className="space-y-2">
                            <label className="text-xs font-bold text-slate-500 uppercase tracking-wider block">Responsable</label>
                            <select
                                className="w-full h-10 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-3 text-sm focus:ring-1 focus:ring-primary outline-none transition-all cursor-pointer"
                                value={filterUser}
                                onChange={(e) => setFilterUser(e.target.value)}
                            >
                                <option value="Tous">Tous les responsables</option>
                                {usersList.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                            </select>
                        </div>
                        <div className="space-y-2">
                            <label className="text-xs font-bold text-slate-500 uppercase tracking-wider block">Entrepôt</label>
                            <select
                                className="w-full h-10 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-3 text-sm focus:ring-1 focus:ring-primary outline-none transition-all cursor-pointer"
                                value={filterWarehouse}
                                onChange={(e) => setFilterWarehouse(e.target.value)}
                            >
                                <option value="Tous">Tous les entrepôts</option>
                                {warehousesList.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
                            </select>
                        </div>
                        <div className="flex items-end">
                            <button
                                onClick={() => { setFilterUser('Tous'); setFilterWarehouse('Tous'); setFilterType('Tous'); setSearchTerm(''); }}
                                className="h-10 px-4 text-primary text-sm font-bold hover:underline"
                            >
                                Tout réinitialiser
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Tableau des Mouvements */}
            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden shadow-sm">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse table-fixed min-w-[900px]">
                        <thead>
                            <tr className="bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-800">
                                <SortableHeader
                                    label="Date & Heure"
                                    field="createdAt"
                                    currentSort={sortBy}
                                    currentOrder={sortOrder}
                                    onSort={(f, o) => { setSortBy(f); setSortOrder(o); }}
                                    className="px-6 py-4 w-[20%]"
                                />
                                <SortableHeader
                                    label="Article (SKU)"
                                    field="product.name"
                                    currentSort={sortBy}
                                    currentOrder={sortOrder}
                                    onSort={(f, o) => { setSortBy(f); setSortOrder(o); }}
                                    className="px-6 py-4 w-[25%]"
                                />
                                <SortableHeader
                                    label="Type"
                                    field="type"
                                    currentSort={sortBy}
                                    currentOrder={sortOrder}
                                    onSort={(f, o) => { setSortBy(f); setSortOrder(o); }}
                                    className="px-6 py-4 text-center justify-center w-[15%]"
                                />
                                <SortableHeader
                                    label="Quantité"
                                    field="quantity"
                                    currentSort={sortBy}
                                    currentOrder={sortOrder}
                                    onSort={(f, o) => { setSortBy(f); setSortOrder(o); }}
                                    className="px-6 py-4 text-right justify-end w-[10%]"
                                />
                                <SortableHeader
                                    label="Emplacement"
                                    field="warehouse.name"
                                    currentSort={sortBy}
                                    currentOrder={sortOrder}
                                    onSort={(f, o) => { setSortBy(f); setSortOrder(o); }}
                                    className="px-6 py-4 w-[15%]"
                                />
                                <th className="px-6 py-4 text-slate-500 dark:text-slate-400 text-xs font-bold uppercase tracking-wider w-[15%]">Responsable</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                            {loading ? (
                                <tr>
                                    <td colSpan="6" className="px-6 py-12 text-center text-slate-400 italic">
                                        Chargement de l'historique...
                                    </td>
                                </tr>
                            ) : filteredMovements.length === 0 ? (
                                <tr>
                                    <td colSpan="6" className="px-6 py-12 text-center text-slate-400 italic font-medium">
                                        <div className="flex flex-col items-center gap-2">
                                            <span className="material-symbols-outlined text-4xl">history</span>
                                            Aucun mouvement trouvé pour ces critères.
                                        </div>
                                    </td>
                                </tr>
                            ) : filteredMovements.map((m) => (
                                <tr key={m.id} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-colors">
                                    <td className="px-6 py-4 text-slate-900 dark:text-slate-100 text-sm">
                                        <div className="font-bold">{new Date(m.createdAt).toLocaleDateString()}</div>
                                        <div className="text-xs text-slate-500 font-medium">{new Date(m.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="text-sm font-black text-slate-900 dark:text-slate-100">{m.product?.sku}</div>
                                        <div className="text-xs text-slate-500 font-medium">{m.product?.name}</div>
                                    </td>

                                    <td className="px-6 py-4 text-center">
                                        <div className="flex flex-col items-center justify-center gap-1">
                                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-tight ${getTypeColor(m.type)}`}>
                                                <span className="material-symbols-outlined text-[14px] mr-1.5 font-bold">{getTypeIcon(m.type)}</span>
                                                {m.type}
                                            </span>
                                            {getPoReference(m.description) && (
                                                <span className="text-[10px] font-bold text-slate-500 bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded border border-slate-200 dark:border-slate-700" title={m.description}>
                                                    Ref: {getPoReference(m.description)}
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 text-right">
                                        <div className="flex flex-col items-end">
                                            <span className={`text-sm font-black ${getQuantityClass(m)}`}>
                                                {m.type === 'Ajustement' ? `Fixé à ${m.quantity}` : `${(m.rawQuantity || m.quantity) > 0 ? '+' : ''}${m.rawQuantity || m.quantity} ${m.unit || 'un.'}`}
                                            </span>
                                            {m.unit && m.unit !== 'Unité' && (
                                                <span className="text-[10px] text-slate-400 font-bold">
                                                    ({Math.abs(m.quantity)} unités)
                                                </span>
                                            )}
                                        </div>
                                    </td>

                                    <td className="px-6 py-4 text-sm font-medium text-slate-600 dark:text-slate-300">
                                        {m.warehouse?.name || 'Inconnu'}
                                    </td>

                                    <td className="px-6 py-4">
                                        <div className="flex items-center gap-2">
                                            {m.user?.avatar ? (
                                                <div className="size-8 rounded-full overflow-hidden border border-primary/20 shrink-0">
                                                    <img src={m.user.avatar} alt="Avatar" className="w-full h-full object-cover" />
                                                </div>
                                            ) : (
                                                <div className="size-8 rounded-full bg-primary/10 text-primary flex items-center justify-center text-[10px] font-black border border-primary/20 shrink-0">
                                                    {m.user?.name ? m.user.name.charAt(0).toUpperCase() : '?'}
                                                </div>
                                            )}
                                            <span className="text-sm font-bold text-slate-700 dark:text-slate-300">{m.user?.name || 'Utilisateur'}</span>

                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                {/* Pagination */}
                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    limit={limit}
                    totalItems={totalMovements}
                    onPageChange={setCurrentPage}
                    onLimitChange={(newLimit) => {
                        setLimit(newLimit);
                        localStorage.setItem('app_items_per_page', newLimit);
                        setCurrentPage(1);
                    }}
                />
            </div>
        </div>
    );
};

export default StockManagement;
